import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyTestimonial = {
  name: '',
  role: '',
  company: '',
  content: '',
  avatar_url: '',
  rating: 5,
  order: 0,
  active: true,
};

const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await adminApi.getTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
    } else {
      setEditingItem({ ...emptyTestimonial, order: testimonials.length });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    const value = e.target.name === 'rating' ? parseInt(e.target.value) || 5 : e.target.value;
    setEditingItem({ ...editingItem, [e.target.name]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingItem.id) {
        await adminApi.updateTestimonial(editingItem.id, editingItem);
        toast.success('Testimonial updated');
      } else {
        await adminApi.createTestimonial(editingItem);
        toast.success('Testimonial created');
      }
      fetchTestimonials();
      closeDialog();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await adminApi.deleteTestimonial(id);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Testimonials</h1>
          <p className="text-muted-foreground">Manage client reviews and feedback.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-testimonial-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Testimonial
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((item) => (
          <div key={item.id} className="admin-card" data-testid={`testimonial-card-${item.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-primary/20">
                {item.avatar_url ? (
                  <img src={item.avatar_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-primary font-bold">
                    {item.name?.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium">{item.name}</p>
                <p className="text-muted-foreground text-sm">{item.role} at {item.company}</p>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mb-4 line-clamp-3">"{item.content}"</p>
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${i < item.rating ? 'text-primary fill-primary' : 'text-muted'}`}
                />
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className={item.active ? 'badge-success' : 'badge-error'}>
                {item.active ? 'Active' : 'Inactive'}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openDialog(item)} data-testid={`edit-testimonial-${item.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-400 hover:text-red-300"
                  data-testid={`delete-testimonial-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {testimonials.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-12">
            No testimonials yet. Add your first review.
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem?.id ? 'Edit Testimonial' : 'Add Testimonial'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Name</label>
                <Input
                  name="name"
                  value={editingItem?.name || ''}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="input-neon"
                  data-testid="testimonial-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <Input
                  name="role"
                  value={editingItem?.role || ''}
                  onChange={handleChange}
                  placeholder="CEO"
                  className="input-neon"
                  data-testid="testimonial-role-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Company</label>
                <Input
                  name="company"
                  value={editingItem?.company || ''}
                  onChange={handleChange}
                  placeholder="TechCorp"
                  className="input-neon"
                  data-testid="testimonial-company-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Rating (1-5)</label>
                <Input
                  name="rating"
                  type="number"
                  min="1"
                  max="5"
                  value={editingItem?.rating || 5}
                  onChange={handleChange}
                  className="input-neon"
                  data-testid="testimonial-rating-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Testimonial Content</label>
              <Textarea
                name="content"
                value={editingItem?.content || ''}
                onChange={handleChange}
                placeholder="Their work exceeded our expectations..."
                rows={4}
                className="input-neon resize-none"
                data-testid="testimonial-content-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Avatar URL (optional)</label>
              <Input
                name="avatar_url"
                value={editingItem?.avatar_url || ''}
                onChange={handleChange}
                placeholder="https://..."
                className="input-neon"
                data-testid="testimonial-avatar-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingItem?.active ?? true}
                onCheckedChange={(checked) => setEditingItem({ ...editingItem, active: checked })}
                data-testid="testimonial-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-testimonial-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsManager;
